import { z } from 'zod';
import { useCompanyId, usePartnerId, useRole } from '../../authentication/hooks/user';
import { ApprovalStatus, Basket, Company, CompanyRegisteredInfo, ContractStatus, Hardware, HaloTicketDto } from '../contracts/companies';
import { PartnerCode } from '../contracts/partners';
import { APIError } from '../errors/APIError';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';
import { useMSPPropertyBag } from './partners';

const mapCompanyEnums = (c: Company): Company => {
  if (typeof c.contractStatus === 'string') {
    c.contractStatus = ContractStatus[c.contractStatus as keyof typeof ContractStatus] ?? c.contractStatus;
  }
  if (typeof c.approvalStatus === 'string') {
    c.approvalStatus = ApprovalStatus[c.approvalStatus as keyof typeof ApprovalStatus] ?? c.approvalStatus;
  }
  return c;
};

export const useCompanies = (partnerId?: string) => {
  return useAPIQuery([`getCompanies`, partnerId], async client => {
    if (!partnerId) return [];
    
    const response = await client.companies.getCompanies({ params: { partnerId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body.items.map(mapCompanyEnums).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  });
};

export const useGetCompanyHaloStock = (mspId?: string, customerId?: string) => {
  return useAPIQuery([`getCompanyHaloStock`, mspId, customerId], async client => {
    if (!mspId || !customerId) return [];
    const response = await client.companies.getCompanyHaloStock({ params: { mspId, customerId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useGetCompanyHaloEquipment = (mspId?: string, customerId?: string) => {
  return useAPIQuery([`getCompanyHaloEquipment`, mspId, customerId], async client => {
    if (!mspId || !customerId) return [];
    const response = await client.companies.getCompanyHaloEquipment({ params: { mspId, customerId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useGetCustomerBundles = (mspId?: string, customerId?: string) => {
  return useAPIQuery([`getCustomerBundles`, mspId, customerId], async client => {
    if (!mspId || !customerId) return [];
    const response = await client.companies.getCustomerBundles({ params: { mspId, customerId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body.items;
  });
};

export const useCompany = (partnerId?: string, id?: string) => {
  return useAPIQuery([`getCompany`, partnerId, id], async client => {
    if (!partnerId || !id) return null as unknown as ReturnType<typeof mapCompanyEnums>;
    const response = await client.companies.getCompany({ params: { partnerId, id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return mapCompanyEnums(response.body);
  });
};

export const useCompanyById = (id: string, customPartnerId?: string) => {
  const myPartnerId = useRole().partnerId;

  const partnerId = customPartnerId || myPartnerId;

  if (!partnerId) {
    throw Error('No partner ID provided');
  }

  const [company] = useCompany(partnerId, id);

  return company;
};

export const useCompanySelectedProducts = (id: string, customPartnerId?: string) => {
  const myPartnerId = useRole().partnerId;

  const partnerId = customPartnerId || myPartnerId;

  if (!partnerId) {
    throw Error('No partner ID provided');
  }

  const [data] = useAPIQuery([`getSelectedProducts`, partnerId, id], async client => {
    const response = await client.companies.getSelectedProducts({ params: { partnerId, id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });

  return data.items;
};

export const useUpdateCompanySelectedProducts = (customerId: string, customPartnerId?: string) => {
  const myPartnerId = useRole().partnerId;

  const partnerId = customPartnerId || myPartnerId;

  if (!partnerId) {
    throw Error('No partner ID provided');
  }

  return useAPIMutation((client, query) => {
    return async (selectedProducts: string[]) => {
      const response = await client.companies.putSelectedProducts({
        params: { partnerId, customerId },
        body: selectedProducts
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['selectedProducts']);
      return response.body;
    };
  });
};

export const useContractApproval = (partnerId: string, id: string) => {
  return useAPIQuery([`getCompanyContractApproval`, partnerId, id], async client => {
    const response = await client.companies.getCompanyContractApproval({ params: { partnerId, id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const usePurchaseOrderApproval = (partnerId: string, id: string) => {
  return useAPIQuery([`getPurchaseOrderApproval`, partnerId, id], async client => {
    const response = await client.companies.getCompanyPurchaseOrderApproval({ params: { partnerId, id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

interface UpdatePurchaseOrderApprovalArgs {
  partnerId: string;
  customerId: string;
  approved: boolean;
}

export const useUpdatePurchaseOrderApproval = () => {
  return useAPIMutation((client, query) => {
    return async ({ partnerId, customerId, approved }: UpdatePurchaseOrderApprovalArgs) => {
      const response = await client.companies.putCompanyPurchaseOrderApproval({
        params: { partnerId, customerId },
        body: { approved }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getPurchaseOrderApproval']);
      return response.body;
    };
  });
};

interface UpdateContractApprovalArgs {
  partnerId: string;
  customerId: string;
  approved: boolean;
}

export const useUpdateContractApproval = () => {
  return useAPIMutation((client, query) => {
    return async ({ partnerId, customerId, approved }: UpdateContractApprovalArgs) => {
      const response = await client.companies.putCompanyContractApproval({
        params: { partnerId, customerId },
        body: { approved }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getCompanyContractApproval']);
      return response.body;
    };
  });
};

export const useGetCustomerContract = () => {
  return useAPIMutation(client => {
    return async ({ id, partnerId }: { id: string; partnerId: string }) => {
      const response = await client.companies.getCompanyContract({ params: { id, partnerId } });
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body;
    };
  });
};

export const useGetCustomerNda = () => {
  return useAPIMutation(client => {
    return async ({ id, partnerId }: { id: string; partnerId: string }) => {
      const response = await client.companies.getCompanyNda({ params: { id, partnerId } });
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body;
    };
  });
};

export interface CreateCompanyNdaArgs {
  regNumber: string;
  regAddress: string;
  signerEmail: string;
  signerName: string;
  signerPosition: string;
}

export const useCreateCompanyNda = (mspId: string, customerId: string) => {
  return useAPIMutation((client, query) => {
    return async (args: CreateCompanyNdaArgs) => {
      const response = await client.companies.createCompanyNda({
        body: {
          regNumber: args.regNumber,
          regAddress: args.regAddress,
          signerEmail: args.signerEmail,
          signerName: args.signerName,
          signerPosition: args.signerPosition,
          returnUrl: `${window.location.origin}/companyOnboarding/msp/${mspId}/customer/${customerId}/ndaCreated`
        },
        params: {
          mspId: mspId,
          customerId: customerId
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getCompanies']);
      return response.body;
    };
  });
};

export const useCreateCompanyContract = (mspId: string, customerId: string) => {
  return useAPIMutation((client, query) => {
    return async () => {
      const response = await client.companies.createCompanyContract({
        body: {
          returnUrl: `${window.location.origin}/companyOnboarding/msp/${mspId}/customer/${customerId}/contractSigned`
        },
        params: {
          mspId: mspId,
          customerId: customerId
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getCompanies']);
      return response.body;
    };
  });
};

export const useCompleteCompanyContract = (mspId: string, customerId: string) => {
  return useAPIMutation((client, query) => {
    return async () => {
      const response = await client.companies.updateCompanyContract({
        body: {},
        params: {
          mspId: mspId,
          customerId: customerId
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getCompanies']);
      return response.body;
    };
  });
};

export interface CreateCompanyArgs {
  name: string;
  code: string;
  registeredInfo: CompanyRegisteredInfo;
}

export const useCreateCompany = (partnerId: string) => {
  const [mspPropertyBag] = useMSPPropertyBag(partnerId);

  return useAPIMutation((client, query) => {
    return async ({ name, code, registeredInfo }: CreateCompanyArgs) => {
      const response = await client.companies.createCompany({
        params: { partnerId },
        body: {
          name,
          code,
          registeredInfo,
          propertyBag: { allowCustomLogo: Boolean(mspPropertyBag?.allowCustomLogo), selectedProductIds: [] }
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getCompanies', partnerId]);
      return response.body;
    };
  });
};

export interface CreateCompaniesArgs {
  companiesInfo: {
    name: string;
    code: string;
    registeredInfo: CompanyRegisteredInfo;
  }[];
}

export const useCreateCompanies = (partnerId: string) => {
  const [mspPropertyBag] = useMSPPropertyBag(partnerId);

  return useAPIMutation((client, query) => {
    return async ({ companiesInfo }: CreateCompaniesArgs) => {
      const payload = companiesInfo.map(item => {
        return {
          ...item,
          propertyBag: { allowCustomLogo: Boolean(mspPropertyBag?.allowCustomLogo), selectedProductIds: [] }
        };
      });

      const response = await client.companies.createCompanies({
        params: { partnerId },
        body: payload
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getCompanies', partnerId]);
      return response.body;
    };
  });
};

export interface UpdateCompaniesArgs {
  companiesInfo: {
    name: string;
    code: string;
    registeredInfo: CompanyRegisteredInfo;
    id: string;
    disabled: boolean;
    approvalStatus: ApprovalStatus;
  }[];
}

export const useUpdateCompanies = (partnerId: string) => {
  const [mspPropertyBag] = useMSPPropertyBag(partnerId);

  return useAPIMutation((client, query) => {
    return async ({ companiesInfo }: UpdateCompaniesArgs) => {
      const payload = companiesInfo.map(item => {
        return {
          ...item,
          propertyBag: { allowCustomLogo: Boolean(mspPropertyBag?.allowCustomLogo), selectedProductIds: [] }
        };
      });

      const response = await client.companies.updateCompanies({
        params: { partnerId },
        body: payload
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getCompanies', partnerId]);
      return response.body;
    };
  });
};
export interface CreateCompanyCodeArgs {
  names: string[];
}

export const useCreateCompanyCode = () => {
  return useAPIMutation(client => {
    return async ({ names }: CreateCompanyCodeArgs) => {
      const response = await client.companies.createCompanyCode({ body: { names } });
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body.items as PartnerCode[];
    };
  });
};

interface DeleteCompanyArgs {
  id: string;
  partnerId: string;
}

export const useDeleteCompany = () => {
  return useAPIMutation(
    client =>
      ({ id, partnerId }: DeleteCompanyArgs) =>
        client.companies.deleteCompany({ params: { partnerId, id } })
  );
};

export const useDisableCustomer = () => {
  return useAPIMutation((client, query) => {
    return async ({ id, partnerId }: DeleteCompanyArgs) => {
      const company = await client.companies.getCompany({ params: { id, partnerId } });
      if (company.status !== 200) throw APIError.fromResponse(company);
      const response = await client.companies.updateCompany({
        params: { id, partnerId },
        body: {
          ...company.body,
          partnerId,
          disabled: true
        }
      });
      if (response.status !== 200) throw APIError.fromResponse(response);
      await query.invalidateQueries(['getCompanies', partnerId]);
      await query.invalidateQueries(['getCompany', partnerId, id]);
      return response.body;
    };
  });
};

export const useEnableCustomer = () => {
  return useAPIMutation((client, query) => {
    return async ({ id, partnerId }: DeleteCompanyArgs) => {
      const company = await client.companies.getCompany({ params: { id, partnerId } });
      if (company.status !== 200) throw APIError.fromResponse(company);
      const response = await client.companies.updateCompany({
        params: { id, partnerId },
        body: {
          ...company.body,
          partnerId,
          disabled: false
        }
      });
      if (response.status !== 200) throw APIError.fromResponse(response);
      await query.invalidateQueries(['getCompanies', partnerId]);
      await query.invalidateQueries(['getCompany', partnerId, id]);
      return response.body;
    };
  });
};

interface UpdateCompanyArgs {
  id: string;
  name: string;
  code: string;
  approvalStatus: ApprovalStatus;
  disabled: boolean;
  registeredInfo: CompanyRegisteredInfo;
  selectedProductIds: string[];
  enableCustomNotifications: boolean;
  haloClientId?: string | null;
  haloGeneralUserId?: string | null;
}

export const useUpdateCompany = (partnerId: string) => {
  const [mspPropertyBag] = useMSPPropertyBag(partnerId);
  return useAPIMutation((client, query) => {
    return async ({
      id,
      name,
      code,
      approvalStatus,
      disabled,
      registeredInfo,
      selectedProductIds,
      enableCustomNotifications,
      haloClientId,
      haloGeneralUserId
    }: UpdateCompanyArgs) => {
      const response = await client.companies.updateCompany({
        params: { partnerId, id },
        body: {
          partnerId,
          name,
          code,
          approvalStatus,
          disabled,
          registeredInfo,
          propertyBag: { allowCustomLogo: Boolean(mspPropertyBag?.allowCustomLogo), selectedProductIds },
          enableCustomNotifications,
          haloClientId,
          haloGeneralUserId
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getCompanies']);
      await query.invalidateQueries(['getCompany']);
      return response.body;
    };
  });
};

interface UpdateCompanyPropertyBagArgs {
  partnerId: string;
  company: Company;
  allowCustomLogo: boolean;
  selectedProductIds: string[];
}

export const useUpdateCompanyProperyBag = () => {
  return useAPIMutation((client, query) => {
    return async ({ partnerId, company, allowCustomLogo, selectedProductIds }: UpdateCompanyPropertyBagArgs) => {
      const response = await client.companies.updateCompanyPropertyBag({
        params: { partnerId, id: company.id },
        body: {
          ...company,
          partnerId,
          propertyBag: { allowCustomLogo, selectedProductIds }
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getCompanies']);
      await query.invalidateQueries(['getCompany']);
      return response.body;
    };
  });
};

interface LookupCompanyArgs {
  name: string;
  postcode: string;
  partnerId: string;
}

export const useLookupCompany = () => {
  return useAPIMutation(client => {
    return ({ name, postcode, partnerId }: LookupCompanyArgs) => {
      return client.companies.lookupCompany({ params: { partnerId }, query: { companyname: name, postcode } });
    };
  });
};

interface LookupCompanyBySafeNoArgs {
  safeNo: string;
  partnerId: string;
}

export const useLookupCompanyBySafeNo = () => {
  return useAPIMutation(client => {
    return ({ safeNo, partnerId }: LookupCompanyBySafeNoArgs) => {
      return client.companies.lookupCompanyBySafeNo({ params: { partnerId }, query: { safeNo } });
    };
  });
};

export const CompanyCheck = z.object({
  failedSections: z.array(z.string()).nullable()
});

export type CompanyCheck = z.infer<typeof CompanyCheck>;

export const useCompanyCheck = (partnerId: string, reportId: string) => {
  return useAPIQuery([`checkCompany`, partnerId, reportId], async client => {
    const response = await client.companies.checkCompany({ params: { partnerId, reportId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

interface UpdateBasketArgs {
  partnerId: string;
  id: string;
  basket: Basket;
}

export const useUpdateBasket = () => {
  return useAPIMutation(client => {
    return ({ partnerId, id, basket }: UpdateBasketArgs) => {
      return client.companies.updateBasket({ params: { partnerId, id }, body: basket });
    };
  });
};

interface SubmitBasketArgs {
  partnerId: string;
  id: string;
}

export const useSubmitBasket = () => {
  return useAPIMutation(client => {
    return ({ partnerId, id }: SubmitBasketArgs) => {
      return client.companies.submitBasket({ params: { partnerId, id } });
    };
  });
};

interface OnboardCompanyArgs {
  partnerId: string;
  companyId: string;
  planId: string;
  creditCheckId: string;
}

export const useOnboardCompany = () => {
  return useAPIMutation(client => {
    return ({ partnerId, companyId, planId, creditCheckId }: OnboardCompanyArgs) => {
      return client.companies.onboardCompany({ params: { partnerId, id: companyId }, body: { planId, creditCheckId } });
    };
  });
};

export const usePartnerOnboardCompany = () => {
  return useAPIMutation(client => {
    return ({ partnerId, companyId, planId, creditCheckId }: OnboardCompanyArgs) => {
      return client.companies.partnerOnboardCompany({
        params: { partnerId },
        body: { planId, creditCheckId, companyId }
      });
    };
  });
};

export const useTemplateCSV = () => {
  const partnerId = usePartnerId();
  const customerId = useCompanyId();

  if (!customerId) {
    throw Error('Needs customer ID');
  }

  const [result] = useAPIQuery([`getTemplate`, partnerId, customerId], async client => {
    const response = await client.companies.getTemplate({ params: { partnerId, id: customerId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
  return result;
};

interface UploadDisposalCsvArgs {
  partnerId: string;
  companyId: string;
  file: File;
}

export const useUploadDisposalCSV = () => {
  return useAPIMutation(client => {
    return ({ partnerId, companyId, file }: UploadDisposalCsvArgs) => {
      const formData = new FormData();
      formData.append('file', file);
      return client.companies.uploadAssetCSV({ params: { partnerId, id: companyId }, body: formData });
    };
  });
};

export const useAgreements = (partnerId: string, id: string) => {
  return useAPIQuery([`getAgreements`, partnerId, id], async client => {
    const response = await client.companies.getAgreements({ params: { partnerId, id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useAgreement = (partnerId: string, id: string, agreementId: string) => {
  return useAPIQuery([`getAgreement`, partnerId, id, agreementId], async client => {
    const response = await client.companies.getAgreement({ params: { partnerId, id, agreementId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useCompanyUsers = (partnerId: string, id: string) => {
  return useAPIQuery(['getCompanyUsers', partnerId, id], async client => {
    const response = await client.companies.getCompanyUsers({ params: { partnerId, id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useMspUsersForCompany = (partnerId: string, id: string) => {
  return useAPIQuery(['getMspUsersForCompany', partnerId, id], async client => {
    const response = await client.companies.getMspUsersForCompany({ params: { partnerId, id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

interface DeleteCompanyUserArgs {
  partnerId: string;
  companyId: string;
  id: string;
}

export const useDeleteCompanyUser = () => {
  return useAPIMutation(client => {
    return ({ partnerId, companyId, id }: DeleteCompanyUserArgs) => {
      return client.companies.deleteCompanyUser({ params: { partnerId, companyId, id } });
    };
  });
};

export interface CreateCompanyHardwareArgs {
  partnerId: string;
  companyId: string;
  manifest: any;
}

export const useCreateCompanyHardware = () => {
  return useAPIMutation(client => {
    return async ({ partnerId, companyId, manifest }: CreateCompanyHardwareArgs) => {
      const response = await client.companies.createCompanyHardware({
        params: { partnerId, companyId },
        body: { manifest }
      });
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body as Hardware;
    };
  });
};

export const useGetCompanyHardware = (partnerId: string, companyId: string) => {
  return useAPIQuery(['getCompanyHardware'], async client => {
    const response = await client.companies.getCompanyHardware({ params: { partnerId, companyId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useGetPartnerHardware = (partnerId: string) => {
  return useAPIQuery(['getPartnerHardware'], async client => {
    const response = await client.companies.getPartnerHardware({ params: { partnerId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useMspDetailsForCustomer = (mspId: string, customerId: string) => {
  return useAPIQuery(['useGetMspDetailsForCustomer', mspId, customerId], async client => {
    const response = await client.companies.getMspDetails({ params: { partnerId: mspId, id: customerId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useCustomerSelectedFinanceModels = (id: string, mspId: string) => {
  return useAPIQuery([`getSelectedFinanceModels`, mspId, id], async client => {
    const response = await client.companies.getSelectedFinanceModels({ params: { partnerId: mspId, id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

interface UpdateCustomerSelectedFinanceModels {
  financeModelIds?: string[];
  peripheralFinanceModelId?: string;
}

export const useUpdateCustomerSelectedFinanceModels = (customerId: string, mspId: string) => {
  return useAPIMutation(
    (client, query) =>
      async ({ financeModelIds, peripheralFinanceModelId }: UpdateCustomerSelectedFinanceModels) => {
        const response = await client.companies.putSelectedFinanceModels({
          params: {
            partnerId: mspId,
            customerId
          },
          body: {
            financeModelIds,
            peripheralFinanceModelId
          }
        });

        if (response.status !== 200) throw APIError.fromResponse(response);

        await query.invalidateQueries(['getSelectedFinanceModels', mspId, customerId]);
        return response.body;
      }
  );
};

export const useGetHaloTickets = (partnerId: string, customerId: string) => {
  return useAPIQuery(['getHaloTickets', partnerId, customerId], async client => {
    try {
      const response = await client.companies.getHaloTickets({ params: { partnerId, id: customerId } });
      if (response.status !== 200) {
        throw APIError.fromResponse(response);
      }
      
      // The backend returns keys with spaces and uppercase letters (e.g. 'Ticket ID').
      // We map them to our camelCase DTO here since ts-rest doesn't auto-transform responses.
      const rawData = response.body as any[];
      return rawData.map(ticket => ({
        ticketId: ticket['Ticket ID'] || ticket.ticketId || ticket.id,
        summary: ticket['Summary'] || ticket.summary,
        status: ticket['Status'] || ticket.status,
        dateCreated: ticket['Date Created'] || ticket.dateCreated,
        companyId: ticket['Company ID'] || ticket.companyId,
      })) as HaloTicketDto[];
    } catch (error: any) {
      if (error?.status === 400 || error?.status === 404 || error?.message?.includes('Not Mapped')) {
        return null;
      }
      throw error;
    }
  });
};

export const useGetCompanyHaloMapping = (partnerId: string, id: string) => {
  return useAPIQuery(['getCompanyHaloMapping', partnerId, id], async (client) => {
    const response = await client.companies.getCompanyHaloMapping({ params: { partnerId, id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useUpdateCompanyHaloMapping = (partnerId: string, id: string) => {
  return useAPIMutation((client, query) => {
    return async (data: { haloClientId?: string | null; haloGeneralUserId?: string | null }) => {
      const response = await client.companies.updateCompanyHaloMapping({
        params: { partnerId, id },
        body: data
      });
      if (response.status !== 204 && response.status !== 200) throw APIError.fromResponse(response);
      await query.invalidateQueries(['getCompanyHaloMapping', partnerId, id]);
      return response.body;
    };
  });
};
